// Google Analytics (GA4 / gtag.js) — carregado APENAS nas páginas públicas
// (landing institucional e Santinho Digital), nunca dentro da aplicação
// autenticada (rotas sob `/app`). Isso evita enviar PII de eleitores ao Google
// (LGPD) — ver regra "nunca logar PII" no CLAUDE.md.
//
// Como o frontend é um SPA único (mesmo build serve landing, santinho e app),
// não dá para colar a tag no index.html. Em vez disso carregamos o gtag.js
// sob demanda na primeira rota pública e controlamos o page_view manualmente.

const GA_MEASUREMENT_ID = 'G-0PCDG6EJ6T';
// Kill-switch oficial do GA: enquanto `true`, o gtag não envia NENHUM hit
// (inclui page_view de enhanced measurement por history change). Usado para
// silenciar completamente o tracking quando o usuário entra em `/app` na mesma
// sessão SPA depois de ter carregado o script numa página pública.
const GA_DISABLE_KEY = `ga-disable-${GA_MEASUREMENT_ID}`;

let scriptLoaded = false;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Só rastreamos em PRODUÇÃO. dev/staging usam o mesmo bundle e enviariam hits
 * ao mesmo GA property, poluindo a métrica de marketing. Prod = domínio
 * `campanhaflow.com.br` (ou subdomínio de tenant), exceto os hosts de
 * `*.dev.campanhaflow.com.br` e `*.staging.campanhaflow.com.br`.
 */
function isProdHost(hostname: string): boolean {
  if (!hostname.endsWith('campanhaflow.com.br')) return false;
  if (hostname.endsWith('.dev.campanhaflow.com.br')) return false;
  if (hostname.endsWith('.staging.campanhaflow.com.br')) return false;
  return true;
}

/**
 * Uma rota é "pública" (rastreável) quando NÃO está sob `/app` (aplicação
 * autenticada) nem é uma rota de auth (login/convite/reset — infra, não
 * marketing, e podem carregar tokens na URL).
 */
export function isPublicPath(pathname: string): boolean {
  if (pathname === '/app' || pathname.startsWith('/app/')) return false;

  const authPrefixes = ['/login', '/auth', '/invite', '/forgot-password', '/reset-password'];
  if (authPrefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false;

  return true;
}

function loadGtag(): void {
  if (scriptLoaded) return;
  scriptLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  // send_page_view: false → controlamos o page_view manualmente em trackPageView,
  // garantindo que nunca disparamos para rotas de `/app`.
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/**
 * Registra uma visualização de página. Chamado a cada mudança de rota.
 * - Em rota pública: garante o gtag carregado e envia o page_view.
 * - Em rota da app/auth: ativa o kill-switch para suprimir qualquer envio.
 */
export function trackPageView(pathname: string, search = ''): void {
  if (typeof window === 'undefined') return;
  if (!isProdHost(window.location.hostname)) return;

  const isPublic = isPublicPath(pathname);

  // Liga/desliga o GA de forma global antes de qualquer hit.
  (window as unknown as Record<string, boolean>)[GA_DISABLE_KEY] = !isPublic;

  if (!isPublic) return;

  loadGtag();
  window.gtag('event', 'page_view', {
    page_path: pathname + search,
    page_location: window.location.href,
    page_title: document.title,
  });
}
